/**
 * Voice preferences — DEVICE-LOCAL, never sent to the server.
 *
 * Per-user volume and local mute are a listener's private opinion about how
 * loud someone is. Syncing them would mean the server knows you turned a
 * colleague down, and it would follow you onto a machine with different
 * speakers where it is simply wrong. Same for the push-to-talk key, which
 * belongs to a keyboard rather than to an account.
 */
import { createStore, useStoreValue } from '@/lib/tinyStore';
import { DEFAULT_PUSH_TO_TALK } from '../lib/hotkey';

export type VoiceInputMode = 'voice-activity' | 'push-to-talk';

export interface VoicePrefs {
  inputMode: VoiceInputMode;
  /** Binding string, see lib/hotkey.ts. */
  pushToTalkKey: string;
  /** 0..1. Mic level at or below this counts as silence in voice-activity mode. */
  sensitivity: number;
  /** Per-user playback gain, 0..2, keyed by userId. */
  volumes: Readonly<Record<string, number>>;
  /** Locally muted users. They are never told. */
  mutedUserIds: readonly string[];
  /** Join/leave chime for the room you are in. */
  chimes: boolean;
}

const STORAGE_KEY = 'cyphex.voice.prefs';

export const DEFAULT_PREFS: VoicePrefs = {
  inputMode: 'voice-activity',
  pushToTalkKey: DEFAULT_PUSH_TO_TALK,
  sensitivity: 0.08,
  volumes: {},
  mutedUserIds: [],
  chimes: true,
};

function load(): VoicePrefs {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<VoicePrefs>;
    // Spread over the defaults: a preference added in a later release must not
    // arrive as `undefined` on a device that stored the older shape.
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return DEFAULT_PREFS;
  }
}

export const voicePrefsStore = createStore<VoicePrefs>(load());

function persist(next: VoicePrefs): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* Private mode or a full quota. Preferences stay in memory for this tab. */
  }
}

export const voicePrefs = {
  get: voicePrefsStore.get,

  patch(patch: Partial<VoicePrefs>): void {
    voicePrefsStore.set((prev) => {
      const next = { ...prev, ...patch };
      persist(next);
      return next;
    });
  },

  setVolume(userId: string, volume: number): void {
    const clamped = Math.max(0, Math.min(2, volume));
    voicePrefs.patch({ volumes: { ...voicePrefsStore.get().volumes, [userId]: clamped } });
  },

  toggleMuted(userId: string): void {
    const current = voicePrefsStore.get().mutedUserIds;
    voicePrefs.patch({
      mutedUserIds: current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId],
    });
  },
} as const;

export const useVoicePrefs = (): VoicePrefs => useStoreValue(voicePrefsStore, (state) => state);

/** Effective gain for a user: 0 when locally muted, otherwise their setting. */
export function gainFor(prefs: VoicePrefs, userId: string): number {
  if (prefs.mutedUserIds.includes(userId)) return 0;
  return prefs.volumes[userId] ?? 1;
}
